import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

/**
 * GET /api/store-owner-profiles/
 * Only Admin can view store owners
 */
export const fetchStoreOwnersThunk = createAsyncThunk(
  'storeOwners/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/store-owners/');
      return response.data; // array of StoreOwnerProfile objects
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Not authorized to view store owners');
      }
      return rejectWithValue('Failed to fetch store owners');
    }
  }
);

/**
 * GET /api/store-owner-profiles/:id/
 * Only Admin can view individual store owners
 */
export const fetchStoreOwnerThunk = createAsyncThunk(
  'storeOwners/fetchOne',
  async (ownerId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/store-owners/${ownerId}/`);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Not authorized to view this store owner');
      }
      return rejectWithValue('Failed to fetch store owner');
    }
  }
);

/**
 * UPDATED: Create store owner (Admin only)
 * Now handles both FormData and regular objects
 */
export const createStoreOwnerThunk = createAsyncThunk(
  'storeOwners/create',
  async (ownerData, { rejectWithValue }) => {
    try {
      // Determine if we're sending FormData or regular JSON
      const isFormData = ownerData instanceof FormData;
      
      // Set appropriate headers based on data type
      const config = {
        headers: {}
      };
      
      if (isFormData) {
        // Let axios set the multipart/form-data boundary automatically
        config.headers['Content-Type'] = 'multipart/form-data';
      } else {
        config.headers['Content-Type'] = 'application/json';
      }
      
      const response = await axiosInstance.post('/api/store-owners/', ownerData, config);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can create store owners');
      }
      
      if (err.response?.status === 400) {
        const errorData = err.response?.data;
        
        if (typeof errorData === 'object' && errorData !== null) {
          const errorMessages = [];
          
          Object.entries(errorData).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              errorMessages.push(`${field}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              errorMessages.push(`${field}: ${messages}`);
            }
          });
          
          if (errorMessages.length > 0) {
            return rejectWithValue(errorMessages.join('; '));
          }
        }
        
        return rejectWithValue(errorData?.detail || 'Invalid data provided');
      }
      
      if (err.response?.status === 413) {
        return rejectWithValue('File size too large. Please choose a smaller profile image.');
      }
      
      if (err.response?.status === 415) {
        return rejectWithValue('Unsupported file type. Please use JPG, PNG, or GIF images.');
      }
      
      return rejectWithValue('Failed to create store owner');
    }
  }
);

/**
 * UPDATED: Update store owner (Admin only)
 * Now handles both FormData and regular objects
 */
export const updateStoreOwnerThunk = createAsyncThunk(
  'storeOwners/update',
  async ({ ownerId, updates }, { rejectWithValue }) => {
    try {
      // Determine if we're sending FormData or regular JSON
      const isFormData = updates instanceof FormData;
      
      // Set appropriate headers based on data type
      const config = {
        headers: {}
      };
      
      if (isFormData) {
        // Let axios set the multipart/form-data boundary automatically
        config.headers['Content-Type'] = 'multipart/form-data';
      } else {
        config.headers['Content-Type'] = 'application/json';
      }
      
      const response = await axiosInstance.patch(`/api/store-owners/${ownerId}/`, updates, config);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can update store owners');
      }
      
      if (err.response?.status === 400) {
        const errorData = err.response?.data;
        
        if (typeof errorData === 'object' && errorData !== null) {
          const errorMessages = [];
          
          Object.entries(errorData).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              errorMessages.push(`${field}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              errorMessages.push(`${field}: ${messages}`);
            }
          });
          
          if (errorMessages.length > 0) {
            return rejectWithValue(errorMessages.join('; '));
          }
        }
        
        return rejectWithValue(errorData?.detail || 'Invalid update data provided');
      }
      
      if (err.response?.status === 413) {
        return rejectWithValue('File size too large. Please choose a smaller profile image.');
      }
      
      if (err.response?.status === 415) {
        return rejectWithValue('Unsupported file type. Please use JPG, PNG, or GIF images.');
      }
      
      return rejectWithValue('Failed to update store owner');
    }
  }
);

/**
 * Delete store owner (Admin only)
 */
export const deleteStoreOwnerThunk = createAsyncThunk(
  'storeOwners/delete',
  async (ownerId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/store-owners/${ownerId}/`);
      return ownerId; // Return ID for state updates
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can delete store owners');
      }
      return rejectWithValue('Failed to delete store owner');
    }
  }
);